import { IconExit, IconMuseSolid, IconUser } from '@/components/icons'
import { AxiosResponse } from 'axios'
import { UserProfile } from 'hooks/useUserProfile'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { signIn, signOut } from 'next-auth/react'
import Image from 'next/image'
import { useMemo } from 'react'
import { KeyedMutator } from 'swr'
import { HomeViewType } from 'types/home'

const homeViewTypeAtom = atomWithStorage<HomeViewType>('MuseAI:bot:homeViewType', HomeViewType.Default)

export function useHomeViewType() {
  return useAtom(homeViewTypeAtom)
}

export interface HomePageProps {
  userProfile?: UserProfile
  onGetUserProfile?: KeyedMutator<AxiosResponse<UserProfile, any>>
  needLogin?: boolean
}

export default function MainContent({ userProfile, needLogin }: HomePageProps) {
  const [_, setHomeViewType] = useHomeViewType()
  const { discordUserId } = userProfile?.userDiscord || {}
  const needBindDiscord = !discordUserId
  const needBindWeixin = useMemo(() => !userProfile?.userWeixin?.openId, [userProfile?.userWeixin?.openId])

  return (
    <>
      <div className="relative flex flex-col md:min-h-screen justify-center p-0">
        <div className="sm:mx-auto sm:w-full flex flex-col justify-center items-center pt-[30px] md:pt-10 px-5 sm:px-10">
          <IconMuseSolid className="w-[50px] h-[50px]" />
          <h2 className="mt-6 text-center text-2xl font-bold tracking-tight">MuseAI Bot</h2>
          <p className="mt-2 text-center text-sm text-basic-600 mb-3 max-w-[312px] md:max-w-none">
            请选择需要使用的助手，智能采集素材至您的 Muse 素材库
          </p>
          <div className="block mb-[30px] md:mb-[50px]">
            {needLogin ? (
              <button
                className="h-[38px] bg-white text-foreground inline-flex space-x-2 items-center rounded-xl shadow-md text-sm py-1.5 px-4 leading-[26px] transition-colors hover:text-basic-600"
                onClick={() => {
                  signIn('muse')
                }}
              >
                <span>登录 MuseDAM 账户</span>
                <IconUser className="w-4 h-4" />
              </button>
            ) : (
              <button
                className="h-[38px] bg-white text-foreground inline-flex space-x-2 items-center rounded-xl shadow-md text-sm py-1.5 px-4 leading-[26px] transition-colors hover:text-basic-600"
                onClick={() => {
                  signOut({ redirect: false })
                  setHomeViewType(HomeViewType.Default)
                }}
              >
                <span>退出 MuseDAM 账户</span>
                <IconExit className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="sm:mx-auto sm:w-full sm:max-w-md md:max-w-4xl px-5 mb-[40px]">
          <div className="text-sm flex flex-col items-center md:flex-row justify-around gap-[30px] md:gap-5">
            {/* wechat info */}
            <div
              className={`bg-white p-4 rounded-xl shadow sm:rounded-lg transition-shadow relative overflow-hidden w-[312px] hover:shadow-xl`}
            >
              <Image src="/screenshot-wechat.png" width={1120} height={824} className="w-full mb-4" alt="微信公众号" />
              <h1 className="text-[20px] font-medium mb-3">微信公众号助手</h1>
              <p className="text-[13px] text-basic-550 mb-6">
                发送链接到特赞Muse微信公众号，快速采集小红书、抖音、微博、微信公众号的内容
              </p>
              <div className="w-full">
                <button
                  className="bg-black w-full p-3 leading-4 rounded text-white transition-all flex space-x-2 items-center justify-center hover:bg-black/80"
                  onClick={() => {
                    setHomeViewType(HomeViewType.Wechat)
                  }}
                >
                  {needBindWeixin ? '开启使用' : '已开启'}
                </button>
              </div>
            </div>
            {/* end wechat info */}

            {/* discord info */}
            <div
              className={`bg-white p-4 rounded-xl shadow sm:rounded-lg transition-shadow relative overflow-hidden w-[312px] hover:shadow-xl`}
            >
              <Image
                src="/screenshot-discord.png"
                width={1120}
                height={824}
                className="w-full mb-4"
                alt="Discord Bot"
              />
              <h1 className="text-[20px] font-medium mb-3">MuseAI Discord Bot</h1>
              <p className="text-[13px] text-basic-550 mb-6">
                高效一键同步 Midjourney 创作者在 Discord 频道里的 作品，并可保存作品提示词
              </p>
              <div className="w-full">
                <button
                  className="bg-black w-full p-3 leading-4 rounded text-white transition-all flex space-x-2 items-center justify-center hover:bg-black/80"
                  onClick={() => {
                    setHomeViewType(HomeViewType.Discord)
                  }}
                >
                  {needBindDiscord ? '开启使用' : '已开启'}
                </button>
              </div>
            </div>

            {/* end discord info */}
          </div>
        </div>
      </div>

      <div className="relative md:fixed md:left-[40px] md:bottom-[30px] flex space-x-2 justify-center items-center pb-[30px] md:pb-0 w-full md:w-auto text-center">
        <a href="https://app.museai.cc/" className="text-basic-550 hover:text-basic-600 text-xs">
          MuseAI
        </a>
        <span className="text-basic-550 text-xs">|</span>
        <a href="https://musedam.cc/" className="text-basic-550 hover:text-basic-600 text-xs">
          MuseDAM
        </a>
      </div>
    </>
  )
}
